import { getApiConfig } from '@src/config/api';
import { getMockModeFromStorage, setMockModeToStorage } from '@src/modules/local_storage_utils';
import { useCallback, useEffect, useState } from 'react';

/**
 * モックモードの状態を管理するカスタムフック（最適化版）
 */
export function useMockMode() {
  const [isMockMode, setIsMockMode] = useState<boolean>(false);
  const [apiConfig, setApiConfig] = useState(getApiConfig());

  // 初期化時にローカルストレージから状態を取得
  useEffect(() => {
    const storedMockMode = getMockModeFromStorage();
    const apiConfig = getApiConfig();

    setIsMockMode(storedMockMode ?? apiConfig.isMockMode);
    setApiConfig(apiConfig);
  }, []);

  /**
   * モックモードを切り替え（最適化版 - ページリロードなし）
   */
  const toggleMockMode = useCallback(() => {
    const newMockMode = !isMockMode;
    setMockModeToStorage(newMockMode);
    setIsMockMode(newMockMode);

    // API設定を即座に更新
    const newApiConfig = getApiConfig();
    setApiConfig(newApiConfig);

    // カスタムイベントを発火して他のコンポーネントに通知
    window.dispatchEvent(
      new CustomEvent('mock-mode-changed', {
        detail: { isMockMode: newMockMode, apiConfig: newApiConfig },
      })
    );
  }, [isMockMode]);

  /**
   * モックモードを設定（最適化版 - ページリロードなし）
   */
  const setMockMode = useCallback((mockMode: boolean) => {
    setMockModeToStorage(mockMode);
    setIsMockMode(mockMode);

    // API設定を即座に更新
    const newApiConfig = getApiConfig();
    setApiConfig(newApiConfig);

    // カスタムイベントを発火
    window.dispatchEvent(
      new CustomEvent('mock-mode-changed', {
        detail: { isMockMode: mockMode, apiConfig: newApiConfig },
      })
    );
  }, []);

  /**
   * モックモードをリセット（通常モードに戻す）
   */
  const resetToNormalMode = useCallback(() => {
    setMockModeToStorage(false);
    setIsMockMode(false);

    // API設定を即座に更新
    const newApiConfig = getApiConfig();
    setApiConfig(newApiConfig);

    // カスタムイベントを発火
    window.dispatchEvent(
      new CustomEvent('mock-mode-changed', {
        detail: { isMockMode: false, apiConfig: newApiConfig },
      })
    );
  }, []);

  return {
    isMockMode,
    apiConfig,
    toggleMockMode,
    setMockMode,
    resetToNormalMode,
  };
}
